/**
 * Relatie-duplicaat-identiteit (besluit 2026-06-18). Twee relaties zijn
 * "dezelfde" (klonen) wanneer **type + naam + richting** identiek zijn:
 *
 *  - **Naam**: bilinguaal (nl én en), trim + case-insensitive, leeg == leeg.
 *  - **Ongerichte Association** (`archimate.Association` met `directed !== true`):
 *    richting-agnostisch — A→B telt als gelijk aan B→A. Beide moeten ongericht
 *    zijn (gericht ≠ ongericht).
 *  - **Al het andere** (gerichte types zoals Serving/Realization, óf een
 *    gerichte Association): geordend — zelfde source→target.
 *
 * Gebruikt door de duplicaat-detectie (onConnect + patch-hook) en het
 * rechtermuis-"Samenvoegen"-menu.
 */
import type { Relation } from "@ae/domain";

/** Minimale vorm voor de identiteit-vergelijking (zodat onConnect 'm kan
 *  aanroepen met een nog-niet-bestaande relatie). */
export type RelationIdentityShape = Pick<
  Relation,
  "typeId" | "label" | "sourceConceptId" | "targetConceptId" | "attributes"
>;

const norm = (s: string | undefined): string => (s ?? "").trim().toLowerCase();

function isUndirectedAssociation(r: RelationIdentityShape): boolean {
  return r.typeId === "archimate.Association" && r.attributes?.directed !== true;
}

/**
 * Twee labels gelden als "dezelfde naam" wanneer:
 *  - geen enkele taal CONFLICTeert (waar beide een niet-lege waarde hebben,
 *    moeten die gelijk zijn), én
 *  - beide naamloos zijn, óf beide genoemd.
 * Zo matcht een en-only label `{en:"aaa"}` met een bilinguaal
 * `{en:"aaa",nl:"aaa"}` (de ontbrekende nl is geen conflict), terwijl "aaa"
 * niet matcht met "bbb" en een naamloze niet met een genoemde relatie.
 */
function labelsMatch(
  a: RelationIdentityShape["label"],
  b: RelationIdentityShape["label"],
): boolean {
  const anl = norm(a?.nl);
  const bnl = norm(b?.nl);
  const aen = norm(a?.en);
  const ben = norm(b?.en);
  if (anl && bnl && anl !== bnl) return false; // nl-conflict
  if (aen && ben && aen !== ben) return false; // en-conflict
  return !!(anl || aen) === !!(bnl || ben); // beide naamloos óf beide genoemd
}

export function isSameRelationIdentity(
  a: RelationIdentityShape,
  b: RelationIdentityShape,
): boolean {
  if (a.typeId !== b.typeId) return false;
  if (!labelsMatch(a.label, b.label)) return false;
  const aU = isUndirectedAssociation(a);
  const bU = isUndirectedAssociation(b);
  if (aU !== bU) return false; // gericht ≠ ongericht
  if (aU && bU) {
    // Ongeordend: richting maakt niet uit.
    return (
      (a.sourceConceptId === b.sourceConceptId && a.targetConceptId === b.targetConceptId) ||
      (a.sourceConceptId === b.targetConceptId && a.targetConceptId === b.sourceConceptId)
    );
  }
  // Geordend.
  return a.sourceConceptId === b.sourceConceptId && a.targetConceptId === b.targetConceptId;
}

/** Live (niet-tombstoned) relaties die identiek zijn aan `shape`. Sluit een
 *  optionele `excludeId` uit (de relatie zelf bij een patch-check). */
export function findDuplicateRelations(
  shape: RelationIdentityShape,
  all: Relation[],
  excludeId?: string,
): Relation[] {
  return all.filter(
    (o) =>
      o.id !== excludeId &&
      !o.tombstoneInBranch &&
      !o.typeId.startsWith("drawing.") &&
      isSameRelationIdentity(shape, o),
  );
}
